import { _ } from "../../../../core/polyglot/polyglot.js";
import { unique } from "@foretagsplatsen/core-helpers/array";
import AbstractForecastReport from "./AbstractForecastReport.js";
import accountRowVisibilityFactory from "../../../reports/accountRowVisibilities/accountRowVisibilityFactory.js";
import IncomeStatementChartStrategy from "../../../../accounting/forecastEditor/widgets/ForecastReportViewerWidget/IncomeStatementChartStrategy.js";
import IncomeStatementForecastFiscalYearCardRenderingStrategy from "../../../../accounting/forecastEditor/widgets/ForecastReportViewerWidget/fiscalYearCardStrategy/IncomeStatementForecastFiscalYearCardRenderingStrategy.js";
import PeriodChangeAccountOperation from "../../../keyFigure/accountOperations/PeriodChangeAccountOperation.js";
import PeriodRollingSeriesSpecification from "../../instance/PeriodRollingSeriesSpecification.js";
import predictionRuleModelFactory from "../../ruleModels/predictionModel/PredictionRuleModelFactory.js";
import Timespan from "../../../period/Timespan.js";

const profitId = "Profit";
const costsId = "CostsBeforeProfitEntries";
const incomeId = "Income";
const totalValueId = "totalValue";
const predictedValueId = "predictedValue";

/**
 * Report specific to Forecast's Income statement.
 */
export default class ForecastIncomeStatement extends AbstractForecastReport {
	constructor() {
		super(...arguments);

		// Map from a number to an array of values, one per column.
		// Used for the fiscal year card.
		this._totalValuesByYearLength = undefined;

		// Array of values, one per column. Used for the fiscal year
		// card.
		this._predictedValues = undefined;
	}

	getTitle() {
		return _("Profit & Loss");
	}

	getSummaryRowTitle() {
		return _("Profit/loss");
	}

	isIncomeStatement() {
		return true;
	}

	getOperationType() {
		return PeriodChangeAccountOperation.getOperationType();
	}

	getChartStrategy() {
		return new IncomeStatementChartStrategy({
			labels: this._getColumnTitles(),
		});
	}

	getChartValues() {
		const chartValues = [...super.getChartValues().values()];
		return chartValues.filter((values) => values.length);
	}

	getChartDataTestName() {
		return "income-statement-chart";
	}

	getFiscalYearCardRenderingStrategy() {
		return new IncomeStatementForecastFiscalYearCardRenderingStrategy();
	}

	getValuesForCard({ closingColumnIndex }) {
		const yearLength = this._getYearLengthFor({ closingColumnIndex });

		const { openingColumn, closingColumn } =
			this._getOpeningAndClosingColumnsFor({ closingColumnIndex });

		const totalValue =
			this._totalValuesByYearLength?.get(yearLength)?.[
				closingColumnIndex
			];

		let predictedValue;
		let predictionTimespan;

		if (!closingColumn?.isPredictedForecastColumn()) {
			// no prediction, the whole period is actual data:
			predictedValue = null;
			predictionTimespan = null;
		} else if (openingColumn?.isPredictedForecastColumn()) {
			// the whole period is predicted:
			predictedValue = totalValue;

			predictionTimespan = new Timespan({
				start: openingColumn.getPeriod()?.getStart(),
				end: closingColumn.getPeriod()?.getEnd(),
			});
		} else {
			// the period is partially actual data and partially predicted:
			predictedValue = this._predictedValues?.[closingColumnIndex];

			predictionTimespan = new Timespan({
				start: this.getPredictedColumns()[0].getPeriod()?.getStart(),
				end: closingColumn.getPeriod()?.getEnd(),
			});
		}

		return {
			fiscalYear: this._getCardYearFor({ openingColumn, closingColumn }),
			predictionTimespan,
			totalValue,
			predictedValue,
		};
	}

	getSummaryValueKeys() {
		const closingColumnIndex = this.getPredictedClosingMonthColumnIndex();
		const yearLength = this._getYearLengthFor({ closingColumnIndex });
		const id = `${totalValueId}-${yearLength}`;

		return [
			PeriodRollingSeriesSpecification.makeKeyFromModel({
				id,
				operationType: this.getOperationType(),
			}),
		];
	}

	rulesRendererFor(forecastCellInfoWidget) {
		return forecastCellInfoWidget.rulesRendererForIncomeStatement();
	}

	getAvailableRuleModelClasses({ row }) {
		if (row.isGroupRow()) {
			return predictionRuleModelFactory.groupRowClasses();
		}

		return predictionRuleModelFactory.allClasses();
	}

	_getCardYearFor({ openingColumn, closingColumn }) {
		const start = openingColumn.getStart().getYear();
		const end = closingColumn.getStart().getYear();
		const fakeYear = start === end ? end : `${start}-${end}`;

		return { toSmartString: () => fakeYear };
	}

	_addComputationSeriesSpecificationsForCard({ builder }) {
		const totalRollingLengths = unique(this._computeYearLengths());

		for (const rollingLength of totalRollingLengths) {
			builder.withPeriodRollingSeriesSpecification({
				id: `${totalValueId}-${rollingLength}`,
				keyFigureId: profitId,
				rollingLength,
				operationType: this.getOperationType(),
			});
		}

		if (this.getCurrentPeriodColumn().isClosing()) {
			// no period is half reference and half predicted so we
			// don't need any other series:
			return;
		}

		const predictedRollingLength =
			this.getPredictedColumns().findIndex((predictedColumn) =>
				predictedColumn.isClosing(),
			) + 1;

		if (predictedRollingLength > 0) {
			builder.withPeriodRollingSeriesSpecification({
				id: predictedValueId,
				keyFigureId: profitId,
				rollingLength: predictedRollingLength,
				operationType: this.getOperationType(),
			});
		}
	}

	_setDataForCardFromSeries(series) {
		const totalRollingLengths = unique(this._computeYearLengths());
		const operationType = this.getOperationType();

		this._totalValuesByYearLength = new Map();

		for (const rollingLength of totalRollingLengths) {
			const id = `totalValue-${rollingLength}`;
			const values = series
				.getRollingPeriodValues({ id, operationType })
				?.getValues();
			this._totalValuesByYearLength.set(rollingLength, values || []);
		}

		this._predictedValues = series
			.getRollingPeriodValues({
				id: "predictedValue",
				operationType,
			})
			?.getValues();
	}

	_computeYearLengths() {
		const yearLengths = [];
		let currentLength = 0;

		for (const column of this.getColumns()) {
			currentLength++;

			if (column.isClosing()) {
				yearLengths.push(currentLength);
				currentLength = 0;
			}
		}

		if (currentLength > 0) {
			yearLengths.push(currentLength);
		}

		return yearLengths;
	}

	static getDefaultAccountVisibility() {
		return accountRowVisibilityFactory.hideNoDataAccountsVisibility();
	}

	static getType() {
		return "income-statement";
	}

	static getSummaryRowKeyFigureId() {
		return profitId;
	}

	static getChartKeyFigureIds() {
		return [incomeId, costsId];
	}
}
